import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { Button, CardFooter, CompoundButton, makeStyles, mergeClasses, shorthands, Tooltip } from '@fluentui/react-components'
import useViewport, { Viewport } from '../../../hooks/useViewport'
import Card from '../../../components/Card'
import { useFormikContext } from 'formik'
import { getLegalAidCertificateValidationSchema, LegalAidCertificate, LegalAidCertificateDto, SaveCertificate } from './LegalAidCertificateForm'
import { bundleIcon, DocumentPdfFilled, DocumentPdfRegular, LinkDismissFilled, LinkDismissRegular } from '@fluentui/react-icons'
import useCurrency from '../../../hooks/useCurrency'
import { LegalAidCertificateProps } from '../../../pages/File/Registration/LegalAidCertificateOverview'
import { stealthClick } from '../../StealthLink'
import useAxios from '../../../hooks/useAxios'
import LinkInvoiceDialog from './LinkInvoiceDialog'
import { CalculationProps } from './Calculation'
import CreateSettlementInvoiceButton from './CreateSettlementInvoiceButton'
import CreateContributionInvoiceButton from './CreateContributionInvoiceButton'
import { DocumentOpenType } from '../../../context/GlobalContext'
import { useSettings } from '../../../hooks/useSettings'
import { useTranslation } from 'react-i18next'

export type LegalAidCertificateInvoiceProps = LegalAidCertificateProps & { calculation: CalculationProps; saveCertificate: SaveCertificate }
const PdfIcon = bundleIcon(DocumentPdfFilled, DocumentPdfRegular)
const UnlinkIcon = bundleIcon(LinkDismissFilled, LinkDismissRegular)

const useStyles = makeStyles({
    footer: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        '& button': {
            justifyContent: 'start',
        },
        ...shorthands.gap(0),
    },
    footerLarge: shorthands.margin('-12px'),
    footerSmall: shorthands.margin('-8px'),
    buttonGroup: {
        display: 'grid',
        gridTemplateColumns: '1fr auto',
    },
})

export default function Invoices(props: LegalAidCertificateInvoiceProps): ReactElement {
    const styles = useStyles()
    const { isViewport } = useViewport()
    const { values, setFieldValue, setFieldError } = useFormikContext<LegalAidCertificateDto>()
    const { currency } = useCurrency(values.currency)
    const axios = useAxios()
    const { settings } = useSettings()

    const certificate: LegalAidCertificate = useMemo(
        () => props.certificates.data?.find(certificate => certificate.id === props.selected.id),
        [props.selected],
    )

    const { t: translate } = useTranslation()
    const validationSchema = useMemo(() => getLegalAidCertificateValidationSchema(translate), [translate])
    const canCreateInvoice = useMemo(() => values.issued?.length > 0 && validationSchema.isValidSync(values), [values, validationSchema])

    // Lock the create buttons and link dialogs while an invoice is being created/generated. The state lives
    // in the overview so it survives the Formik remount when the selection or certificates list changes.
    const contributionPending = props.invoicePending === 'contribution'
    const settlementPending = props.invoicePending === 'settlement'

    // Link or unlink an invoice through an explicit save: submitting the Formik state right after a
    // setFieldValue races the batched state update and can persist stale values.
    const linkInvoice = (field: 'contributionInvoiceId' | 'settlementInvoiceId', invoiceId: string | null) =>
        props.saveCertificate({ ...values, [field]: invoiceId }, setFieldError, { finalize: true }).then(() => setFieldValue(field, invoiceId))

    return (
        <Card>
            <CardFooter className={mergeClasses(styles.footer, isViewport('<', Viewport.MainMenu) ? styles.footerSmall : styles.footerLarge)}>
                {certificate?.contributionInvoice ? (
                    <div className={styles.buttonGroup}>
                        <CompoundButton
                            disabled={!certificate.contributionInvoice.pdfId}
                            secondaryContent={`Open declaratie eigen bijdrage (${certificate.contributionInvoice.number})`}
                            appearance="subtle"
                            icon={<PdfIcon />}
                            onClick={() =>
                                stealthClick(
                                    { id: certificate.contributionInvoice.pdfId, documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open },
                                    axios,
                                )
                            }
                        >
                            Eigen bijdrage
                        </CompoundButton>
                        <Tooltip
                            content="Ontkoppel declaratie eigen bijdrage van deze toevoeging. Er wordt geen creditnota aangemaakt."
                            relationship="description"
                            withArrow
                        >
                            <Button
                                icon={<UnlinkIcon />}
                                appearance="subtle"
                                size="large"
                                onClick={() => linkInvoice('contributionInvoiceId', null)}
                            />
                        </Tooltip>
                    </div>
                ) : (
                    <div className={styles.buttonGroup}>
                        <CreateContributionInvoiceButton
                            {...props}
                            canCreateInvoice={canCreateInvoice}
                            pending={contributionPending}
                            setPending={pending => props.setInvoicePending(pending ? 'contribution' : undefined)}
                        />
                        <LinkInvoiceDialog
                            disabled={!canCreateInvoice || contributionPending}
                            fileId={props.file.data.id}
                            currency={currency}
                            certificates={props.certificates.data}
                            targetValue={Number(values.contribution)}
                            tooltipContent="Koppel een bestaande declaratie eigen bijdrage aan deze toevoeging."
                            onSubmit={invoiceId => linkInvoice('contributionInvoiceId', invoiceId)}
                        />
                    </div>
                )}
                {certificate?.settlementInvoice ? (
                    <div className={styles.buttonGroup}>
                        <CompoundButton
                            disabled={!certificate.settlementInvoice.pdfId}
                            secondaryContent={`Open eindafrekening RvR (${certificate.settlementInvoice.number})`}
                            appearance="subtle"
                            icon={<PdfIcon />}
                            onClick={() =>
                                stealthClick(
                                    { id: certificate.settlementInvoice.pdfId, documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open },
                                    axios,
                                )
                            }
                        >
                            Eindafrekening RvR
                        </CompoundButton>
                        <Tooltip
                            content="Ontkoppel de declaratie met de RvR eindafrekening van deze declaratie. Er wordt geen creditnota aangemaakt."
                            relationship="description"
                            withArrow
                        >
                            <Button icon={<UnlinkIcon />} appearance="subtle" size="large" onClick={() => linkInvoice('settlementInvoiceId', null)} />
                        </Tooltip>
                    </div>
                ) : (
                    <div className={styles.buttonGroup}>
                        <CreateSettlementInvoiceButton
                            {...props}
                            canCreateInvoice={canCreateInvoice}
                            pending={settlementPending}
                            setPending={pending => props.setInvoicePending(pending ? 'settlement' : undefined)}
                        />
                        <LinkInvoiceDialog
                            disabled={!canCreateInvoice || settlementPending}
                            fileId={props.file.data.id}
                            certificates={props.certificates.data}
                            currency={currency}
                            targetValue={props.calculation.compensation}
                            tooltipContent="Koppel een bestaande declaratie als RvR eindafrekening aan deze toevoeging."
                            onSubmit={invoiceId => linkInvoice('settlementInvoiceId', invoiceId)}
                        />
                    </div>
                )}
            </CardFooter>
        </Card>
    )
}
